import { formatCount } from './format';
import type { ChatReport, Grade, PersonStats, RoastLine } from './types';
import { compactLocalDate, formatAverage, formatCompactPeriod, peopleByMessages, roomTitle } from './ui/format-ui';

/**
 * Share image (spec §6.3): a 1080×1350 PNG drawn on a canvas with the cover,
 * the grade table, two or three awards and the headline. Nothing is uploaded;
 * the user saves or shares the File themselves.
 *
 * The image carries names, grades and award titles, never a word from the
 * chat: the headline is card.shareHeadline (not the on-page headline, which
 * may quote a 말버릇), and the remark fallback skips lines that repeat chat
 * words. The page promises this ("저장 이미지에는 메시지 내용이 들어가지 않아요").
 */

export const SHARE_IMAGE_WIDTH = 1080;
export const SHARE_IMAGE_HEIGHT = 1350;
export const SHARE_IMAGE_MAX_PEOPLE = 4;
export const SHARE_IMAGE_MAX_AWARDS = 3;
export const SHARE_IMAGE_FOOTER = 'oneultool.com · 카톡 성적표';

export interface ShareImageColumn {
  readonly name: string;
  readonly isMe: boolean;
}

export interface ShareImageRow {
  readonly title: string;
  /** One grade per column; null when that subject could not be graded. */
  readonly grades: readonly (Grade | null)[];
}

export interface ShareImageModel {
  readonly title: string;
  readonly period: string;
  readonly meta: string;
  /** 담임 총평 for the image (card.shareHeadline): never a word from the chat. */
  readonly headline: string;
  readonly columns: readonly ShareImageColumn[];
  readonly rows: readonly ShareImageRow[];
  /** People left out of the table (groups with more than four people). */
  readonly omittedPeople: number;
  readonly awards: readonly { readonly title: string; readonly name: string }[];
  /** Fallback content when there are no awards: top remarks without chat words (isShareSafeLine). */
  readonly lines: readonly string[];
}

export function shareImageFileName(now: Date = new Date()): string {
  return `카톡성적표-${compactLocalDate(now)}.png`;
}

/** Remarks that repeat what someone typed (말버릇, 최애 이모지); ids look like "catchphrase:1". */
export const CHAT_WORD_LINE_IDS = ['catchphrase', 'favorite-emoji'] as const;

/** Quote marks: the report copy quotes words with them (‘봐서’, ‘사랑해’). */
const QUOTE_MARKS = /[‘’“”]/;

/** True when a remark may go on the image: not a chat-word rule and nothing quoted. */
export function isShareSafeLine(line: Pick<RoastLine, 'id' | 'text'>): boolean {
  const id = typeof line.id === 'string' ? line.id : '';
  return !CHAT_WORD_LINE_IDS.some((prefix) => id.startsWith(prefix))
    && typeof line.text === 'string'
    && !QUOTE_MARKS.test(line.text);
}

/**
 * The image's 담임 총평: card.shareHeadline (roles and room facts only). Should
 * it ever be missing or quote something, the image uses the plain "n개의
 * 메시지로 채점한…" sentence (same copy as the report's fallback headline), so a
 * regression in the report copy cannot put a chat word on the image.
 */
export function shareImageHeadline(report: ChatReport): string {
  const headline = typeof report.card.shareHeadline === 'string' ? report.card.shareHeadline.trim() : '';
  if (headline && !QUOTE_MARKS.test(headline)) return headline;
  const room = report.stats.roomKind === 'group' ? '우리 방' : '우리 사이';
  const total = report.stats.totals.messages;
  return total > 0 ? `${formatCount(total)}개의 메시지로 채점한 ${room} 성적표예요.` : `${room} 성적표예요.`;
}

export function buildShareImageModel(report: ChatReport): ShareImageModel {
  const { stats, card } = report;
  const byIndex = new Map<number, PersonStats>(stats.people.map((person) => [person.index, person]));
  const ranked = peopleByMessages(stats.people);
  const shown = ranked.slice(0, SHARE_IMAGE_MAX_PEOPLE);
  const shownSet = new Set(shown.map((person) => person.index));
  // Keep the report's own person order inside the table.
  const columns = stats.people.filter((person) => shownSet.has(person.index));

  const picked: typeof card.awards[number][] = [];
  const awarded = new Set<number>();
  for (const award of card.awards) {
    if (picked.length >= SHARE_IMAGE_MAX_AWARDS) break;
    if (awarded.has(award.person) || !byIndex.has(award.person)) continue;
    picked.push(award);
    awarded.add(award.person);
  }
  for (const award of card.awards) {
    if (picked.length >= SHARE_IMAGE_MAX_AWARDS) break;
    if (!picked.includes(award) && byIndex.has(award.person)) picked.push(award);
  }

  return {
    title: roomTitle(stats),
    period: formatCompactPeriod(stats.totals.firstAt, stats.totals.lastAt),
    meta: `메시지 ${formatCount(stats.totals.messages)}개 · 하루 평균 ${formatAverage(stats.totals.perDay)}개 · ${formatCount(stats.totals.spanDays)}일`,
    headline: shareImageHeadline(report),
    columns: columns.map((person) => ({ name: person.name, isMe: person.isMe })),
    rows: card.subjects.map((subject) => ({
      title: subject.title,
      grades: columns.map((person) => subject.scores.find((score) => score.person === person.index)?.grade ?? null),
    })),
    omittedPeople: Math.max(0, stats.people.length - columns.length),
    awards: picked.map((award) => ({ title: award.title, name: byIndex.get(award.person)!.name })),
    lines: card.lines.filter(isShareSafeLine).slice(0, 2).map((line) => line.text),
  };
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

const COLORS = {
  background: '#f4f0ff',
  card: '#ffffff',
  brand: '#5f33e1',
  brandSoft: '#ede8ff',
  ink: '#24252c',
  muted: '#6e6a7c',
  line: '#eeeaf4',
  stripe: '#faf8ff',
};

export const GRADE_COLORS: Readonly<Record<Grade, { readonly background: string; readonly text: string }>> = {
  'A+': { background: '#5f33e1', text: '#ffffff' },
  A: { background: '#ede8ff', text: '#4a22c4' },
  B: { background: '#def3ef', text: '#0d6156' },
  C: { background: '#fff0d2', text: '#7f5200' },
  D: { background: '#efedf3', text: '#545064' },
};

const FONT_STACK = '"Noto Sans KR", "Lexend Deca", system-ui, sans-serif';
const font = (weight: number, size: number) => `${weight} ${size}px ${FONT_STACK}`;

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

/** Ellipsizes text to fit maxWidth with the current font. */
export function fitText(measure: (text: string) => number, text: string, maxWidth: number): string {
  if (measure(text) <= maxWidth) return text;
  const characters = Array.from(text);
  let low = 0;
  let high = characters.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (measure(`${characters.slice(0, middle).join('')}…`) <= maxWidth) low = middle;
    else high = middle - 1;
  }
  return `${characters.slice(0, low).join('').trimEnd()}…`;
}

/** Greedy wrap that prefers spaces and breaks long words by character. */
export function wrapText(measure: (text: string) => number, text: string, maxWidth: number, maxLines: number): string[] {
  const lines: string[] = [];
  let current = '';
  for (const word of text.trim().split(/\s+/)) {
    const candidate = current ? `${current} ${word}` : word;
    if (measure(candidate) <= maxWidth) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    current = '';
    for (const character of Array.from(word)) {
      if (current && measure(current + character) > maxWidth) {
        lines.push(current);
        current = character;
      } else {
        current += character;
      }
    }
  }
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines - 1);
  kept.push(fitText(measure, lines.slice(maxLines - 1).join(' '), maxWidth));
  return kept;
}

/**
 * Like wrapText, but narrows the width while the line count stays the same,
 * so a two-line headline does not end with a lonely last word.
 */
export function balancedWrap(measure: (text: string) => number, text: string, maxWidth: number, maxLines: number): string[] {
  const lines = wrapText(measure, text, maxWidth, maxLines);
  if (lines.length < 2 || wrapText(measure, text, maxWidth, Number.MAX_SAFE_INTEGER).length > maxLines) return lines;
  let low = maxWidth / 2;
  let high = maxWidth;
  for (let step = 0; step < 12; step++) {
    const middle = (low + high) / 2;
    if (wrapText(measure, text, middle, Number.MAX_SAFE_INTEGER).length <= lines.length) high = middle;
    else low = middle;
  }
  return wrapText(measure, text, high, maxLines);
}

export function drawShareImage(ctx: CanvasRenderingContext2D, model: ShareImageModel): void {
  const measure = (text: string) => ctx.measureText(text).width;
  const W = SHARE_IMAGE_WIDTH;
  const H = SHARE_IMAGE_HEIGHT;
  const cardX = 48;
  const cardY = 48;
  const cardW = W - cardX * 2;
  const cardH = 1214;
  const left = cardX + 64;
  const width = cardW - 128;
  const right = left + width;

  // Background with two soft glows, then the white card.
  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, W, H);
  const glow = (x: number, y: number, radius: number, color: string) => {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, 'rgba(244,240,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, W, H);
  };
  glow(120, 80, 520, 'rgba(193,169,255,.55)');
  glow(1020, 760, 520, 'rgba(172,216,255,.45)');
  ctx.save();
  ctx.shadowColor = 'rgba(62,43,118,.12)';
  ctx.shadowBlur = 48;
  ctx.shadowOffsetY = 14;
  roundedRect(ctx, cardX, cardY, cardW, cardH, 44);
  ctx.fillStyle = COLORS.card;
  ctx.fill();
  ctx.restore();

  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  // Kicker pill + period.
  ctx.font = font(700, 26);
  const kicker = '카톡 성적표';
  const pillWidth = measure(kicker) + 44;
  roundedRect(ctx, left, 108, pillWidth, 50, 25);
  ctx.fillStyle = COLORS.brandSoft;
  ctx.fill();
  ctx.fillStyle = COLORS.brand;
  ctx.fillText(kicker, left + 22, 142);
  ctx.font = font(500, 26);
  ctx.fillStyle = COLORS.muted;
  ctx.textAlign = 'right';
  ctx.fillText(fitText(measure, model.period, width - pillWidth - 24), right, 142);
  ctx.textAlign = 'left';

  // Title and meta line.
  ctx.font = font(700, 60);
  ctx.fillStyle = COLORS.ink;
  ctx.fillText(fitText(measure, model.title, width), left, 238);
  ctx.font = font(500, 28);
  ctx.fillStyle = COLORS.muted;
  ctx.fillText(fitText(measure, model.meta, width), left, 290);

  // Headline box (담임 총평): two big lines, or three smaller ones.
  const boxTop = 326;
  const boxPadding = 32;
  const textWidth = width - boxPadding * 2;
  let headlineSize = 38;
  let lineHeight = 52;
  ctx.font = font(700, headlineSize);
  let lines = balancedWrap(measure, model.headline, textWidth, 2);
  if (wrapText(measure, model.headline, textWidth, Number.MAX_SAFE_INTEGER).length > 2) {
    headlineSize = 32;
    lineHeight = 44;
    ctx.font = font(700, headlineSize);
    lines = balancedWrap(measure, model.headline, textWidth, 3);
  }
  const labelHeight = 30;
  const boxHeight = boxPadding + labelHeight + 12 + lines.length * lineHeight + 20;
  roundedRect(ctx, left, boxTop, width, boxHeight, 30);
  ctx.fillStyle = COLORS.brandSoft;
  ctx.fill();
  ctx.font = font(700, 24);
  ctx.fillStyle = COLORS.brand;
  ctx.fillText('담임 총평', left + boxPadding, boxTop + boxPadding + 22);
  ctx.font = font(700, headlineSize);
  ctx.fillStyle = COLORS.ink;
  const textTop = boxTop + boxPadding + labelHeight + 12;
  lines.forEach((line, index) => {
    ctx.fillText(line, left + boxPadding, textTop + lineHeight * index + Math.round(headlineSize * 0.95));
  });

  // Grade table.
  let y = boxTop + boxHeight + 50;
  ctx.font = font(700, 30);
  ctx.fillStyle = COLORS.ink;
  ctx.fillText('과목별 성적', left, y);
  if (model.omittedPeople > 0) {
    ctx.font = font(500, 22);
    ctx.fillStyle = COLORS.muted;
    ctx.textAlign = 'right';
    ctx.fillText(`메시지가 많은 ${model.columns.length}명만 표시`, right, y);
    ctx.textAlign = 'left';
  }
  y += 22;
  const subjectWidth = 292;
  const columnCount = Math.max(1, model.columns.length);
  const columnWidth = (width - subjectWidth) / columnCount;
  const headerHeight = 50;
  ctx.font = font(700, 24);
  ctx.fillStyle = COLORS.muted;
  ctx.textAlign = 'center';
  model.columns.forEach((column, index) => {
    const label = column.isMe ? `${column.name} (나)` : column.name;
    ctx.fillText(fitText(measure, label, columnWidth - 16), left + subjectWidth + columnWidth * (index + 0.5), y + 34);
  });
  ctx.textAlign = 'left';
  y += headerHeight;
  const rowHeight = 60;
  model.rows.forEach((row, rowIndex) => {
    const top = y + rowIndex * rowHeight;
    if (rowIndex % 2 === 0) {
      roundedRect(ctx, left, top, width, rowHeight, 16);
      ctx.fillStyle = COLORS.stripe;
      ctx.fill();
    }
    ctx.font = font(500, 27);
    ctx.fillStyle = COLORS.ink;
    ctx.fillText(fitText(measure, row.title, subjectWidth - 32), left + 22, top + 40);
    if (row.grades.every((grade) => grade === null)) {
      ctx.font = font(500, 23);
      ctx.fillStyle = COLORS.muted;
      ctx.textAlign = 'center';
      ctx.fillText('채점 보류', left + subjectWidth + (width - subjectWidth) / 2, top + 39);
      ctx.textAlign = 'left';
      return;
    }
    row.grades.forEach((grade, index) => {
      const centerX = left + subjectWidth + columnWidth * (index + 0.5);
      if (!grade) {
        ctx.font = font(500, 26);
        ctx.fillStyle = COLORS.muted;
        ctx.textAlign = 'center';
        ctx.fillText('–', centerX, top + 40);
        ctx.textAlign = 'left';
        return;
      }
      const colors = GRADE_COLORS[grade];
      const chipWidth = Math.min(96, columnWidth - 18);
      roundedRect(ctx, centerX - chipWidth / 2, top + 9, chipWidth, 42, 21);
      ctx.fillStyle = colors.background;
      ctx.fill();
      ctx.font = font(700, 26);
      ctx.fillStyle = colors.text;
      ctx.textAlign = 'center';
      ctx.fillText(grade, centerX, top + 39);
      ctx.textAlign = 'left';
    });
  });
  y += model.rows.length * rowHeight + 48;

  // Awards (or the top remarks when no award applies).
  const bottom = cardY + cardH - 52;
  if (model.awards.length > 0) {
    ctx.font = font(700, 30);
    ctx.fillStyle = COLORS.ink;
    ctx.fillText('상장', left, y);
    y += 22;
    const gap = 16;
    const awardWidth = (width - gap * (model.awards.length - 1)) / model.awards.length;
    const awardHeight = Math.min(112, bottom - y);
    model.awards.forEach((award, index) => {
      const x = left + index * (awardWidth + gap);
      roundedRect(ctx, x, y, awardWidth, awardHeight, 24);
      ctx.fillStyle = '#fff8e6';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x + 40, y + awardHeight / 2, 18, 0, Math.PI * 2);
      ctx.fillStyle = '#f2b636';
      ctx.fill();
      ctx.font = font(700, 20);
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText('상', x + 40, y + awardHeight / 2 + 7);
      ctx.textAlign = 'left';
      const textLeft = x + 72;
      const textWidthAvailable = awardWidth - 88;
      ctx.font = font(700, 22);
      ctx.fillStyle = '#8a5a00';
      ctx.fillText(fitText(measure, award.title, textWidthAvailable), textLeft, y + awardHeight / 2 - 6);
      ctx.font = font(700, 28);
      ctx.fillStyle = COLORS.ink;
      ctx.fillText(fitText(measure, award.name, textWidthAvailable), textLeft, y + awardHeight / 2 + 30);
    });
  } else if (model.lines.length > 0) {
    ctx.font = font(700, 30);
    ctx.fillStyle = COLORS.ink;
    ctx.fillText('담임 한마디', left, y);
    y += 50;
    ctx.font = font(500, 27);
    ctx.fillStyle = COLORS.ink;
    for (const line of model.lines) {
      for (const part of wrapText(measure, `· ${line}`, width, 2)) {
        if (y > bottom) break;
        ctx.fillText(part, left, y);
        y += 40;
      }
      y += 8;
    }
  }

  // Footer under the card.
  ctx.font = font(600, 26);
  ctx.fillStyle = COLORS.muted;
  ctx.textAlign = 'center';
  ctx.fillText(SHARE_IMAGE_FOOTER, W / 2, H - 30);
  ctx.textAlign = 'left';
}

async function waitForFonts(timeoutMs = 4_000): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  const loads = Promise.all([
    document.fonts.ready,
    document.fonts.load(`700 40px "Noto Sans KR"`, '카톡 성적표'),
    document.fonts.load(`500 28px "Noto Sans KR"`, '카톡 성적표'),
    document.fonts.load(`600 26px "Noto Sans KR"`, '카톡 성적표'),
  ]).then(() => undefined, () => undefined);
  await Promise.race([loads, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

/** Renders the PNG. Call ahead of the save tap (iOS needs the share in the tap). */
export async function createShareImageFile(report: ChatReport, now: Date = new Date()): Promise<File> {
  await waitForFonts();
  const canvas = document.createElement('canvas');
  canvas.width = SHARE_IMAGE_WIDTH;
  canvas.height = SHARE_IMAGE_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('이 브라우저에서는 성적표 이미지를 만들 수 없어요.');
  drawShareImage(context, buildShareImageModel(report));
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => (value ? resolve(value) : reject(new Error('성적표 이미지를 만들지 못했어요.'))), 'image/png');
  });
  return new File([blob], shareImageFileName(now), { type: 'image/png' });
}
